import { createClient, type JuvoClient } from '@juvo/sdk';
import type { PublicUser } from '@juvo/schemas';
import type { Locale, MessageKey } from './i18n/messages.js';
import { messages } from './i18n/messages.js';

const TOKEN_KEY = 'jv_access';
const LOCALE_KEY = 'jv_locale';
const CART_KEY = 'jv_cart';

type Listener = () => void;

export interface CartLine {
  sku: string;
  qty: number;
}

class AppStore {
  readonly client: JuvoClient;
  user: PublicUser | null = null;
  locale: Locale = 'fr';
  cart: CartLine[] = [];
  private listeners = new Set<Listener>();

  constructor() {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    this.client = createClient(apiUrl);
    const savedLocale = localStorage.getItem(LOCALE_KEY);
    if (savedLocale === 'fr' || savedLocale === 'en') this.locale = savedLocale;
    document.documentElement.lang = this.locale;
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (token) this.client.setToken(token);
    this.loadCart();
  }

  t(key: MessageKey): string {
    return messages[this.locale][key];
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  setLocale(locale: Locale) {
    this.locale = locale;
    localStorage.setItem(LOCALE_KEY, locale);
    document.documentElement.lang = locale;
    this.emit();
  }

  async hydrate() {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      this.user = await this.client.me();
      this.emit();
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      this.client.setToken(null);
      this.user = null;
      this.emit();
    }
  }

  setSession(user: PublicUser, accessToken: string) {
    this.user = user;
    this.client.setToken(accessToken);
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    this.emit();
  }

  async logout() {
    await this.client.logout();
    sessionStorage.removeItem(TOKEN_KEY);
    this.user = null;
    this.emit();
  }

  get isStaff() {
    return !!this.user?.roles.some((role) => role === 'admin' || role === 'juvo-staff');
  }

  get cartCount() {
    return this.cart.reduce((sum, line) => sum + line.qty, 0);
  }

  addToCart(sku: string, qty = 1) {
    const existing = this.cart.find((line) => line.sku === sku);
    if (existing) existing.qty += qty;
    else this.cart = [...this.cart, { sku, qty }];
    this.persistCart();
  }

  setCartQty(sku: string, qty: number) {
    if (qty <= 0) this.cart = this.cart.filter((line) => line.sku !== sku);
    else this.cart = this.cart.map((line) => (line.sku === sku ? { ...line, qty } : line));
    this.persistCart();
  }

  clearCart() {
    this.cart = [];
    this.persistCart();
  }

  private loadCart() {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as CartLine[];
      if (Array.isArray(parsed)) this.cart = parsed.filter((line) => line.sku && line.qty > 0);
    } catch {
      this.cart = [];
    }
  }

  private persistCart() {
    localStorage.setItem(CART_KEY, JSON.stringify(this.cart));
    this.emit();
  }
}

export const store = new AppStore();
